
import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { FileUpIcon } from 'lucide-react';
import { Progress } from '@/components/ui/progress';

interface UploadButtonProps {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  progress: number | null;
  accept?: string;
  label?: string;
}

export const UploadButton: React.FC<UploadButtonProps> = ({
  onUpload,
  isUploading,
  progress,
  accept = '*',
  label = 'Upload Document'
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      await onUpload(file);
      
      // Reset the input value so the same file can be uploaded again if needed
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  return (
    <div className="space-y-2">
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept={accept}
        className="hidden"
      />
      <Button
        onClick={handleClick}
        disabled={isUploading}
        className="flex items-center gap-2"
      >
        <FileUpIcon className="h-4 w-4" />
        {label}
      </Button>
      
      {isUploading && progress !== null && (
        <div className="w-full space-y-1">
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-muted-foreground text-right">{progress}%</p>
        </div>
      )}
    </div>
  );
};
